import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, MenuItem, Switch, FormControlLabel, CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useHasPermission } from "../utils/permissionUtils";
import styled from "@emotion/styled";
import { createClient } from "../redux/ducks/client";
import TagPicker, { splitTagAssignments } from "./TagPicker";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
}));

const FormField = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "top",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
}));

const Column = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.h6,
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: "100%",
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CreateClientDialog = ({ isLoading, open, onClose, onSubmit, managers = [] }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const canSeePrivate = hasPermission("canSeePrivate");
    const canEditBillingTerms = hasPermission("editBillingTerms");
    const [formErrors, setFormErrors] = useState({});
    const [tagEntries, setTagEntries] = useState([]);

    useEffect(() => {
        if (!open) setTagEntries([]);
    }, [open]);
    const [clientData, setClientData] = useState({
        name: "",
        active: true,
        address: {
            street: "",
            apartment: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
        },
        email: "",
        phone: "",
        website: "",
        logoURL: "",
        clientManager: null,
        primaryContact: null,
        signedDocuments: [
            { document: 'Service Agreement', signed: false },
            { document: 'Non-Disclosure Agreement', signed: false },
            { document: 'Credit Card Authorization', signed: false },
        ],
        billingTerms: {
            type: "Hourly",
            hours: 0,
            charge: 0,
            overage: false,
            frequency: "Monthly",
            vip: false,
            level: 0,
        },
        privacy: "Public",
        accounts: [],
        isDeleted: false,
        isInternal: false,
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value?.trim() === '' ? 'Client name is required' : '';
            case 'email':
                if (!value || value.trim() === '') {
                    return 'Email is required';
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return !emailRegex.test(value) ? 'Invalid email format' : '';
            case 'website':
                if (value && value.trim() !== '') {
                    try {
                        // Try to parse as-is first
                        new URL(value);
                        return '';
                    } catch {
                        // If that fails, try with https:// prefix
                        try {
                            new URL(`https://${value}`);
                            return '';
                        } catch {
                            return 'Invalid URL format';
                        }
                    }
                }
                return '';
            case 'phone':
                if (!value || value.trim() === '') {
                    return 'Phone is required';
                }
                const phoneRegex = new RegExp("^\\+?[\\d\\s\\-()]{1,25}$");
                return !phoneRegex.test(value) ? 'Invalid phone number format' : '';
            case 'billingHours':
                if (clientData.billingTerms.type === 'Retainer') {
                    const hours = parseFloat(value);
                    return isNaN(hours) || hours <= 0 ? 'Hours must be a positive number' : '';
                }
                return '';
            case 'billingCharge':
                if (clientData.billingTerms.type === 'Retainer') {
                    const charge = parseFloat(value);
                    return isNaN(charge) || charge <= 0 ? 'Charge must be a positive number' : '';
                }
                return '';
            default:
                return '';
        }
    };

    const validateAllFields = () => {
        const errors = {};
        let isValid = true;

        // Validate required fields
        const nameError = validateField('name', clientData.name);
        if (nameError) {
            errors.name = nameError;
            isValid = false;
        }

        // Validate email (required)
        const emailError = validateField('email', clientData.email);
        if (emailError) {
            errors.email = emailError;
            isValid = false;
        }

        // Validate phone (required)
        const phoneError = validateField('phone', clientData.phone);
        if (phoneError) {
            errors.phone = phoneError;
            isValid = false;
        }

        // Validate website if provided
        const websiteError = validateField('website', clientData.website);
        if (websiteError) {
            errors.website = websiteError;
            isValid = false;
        }

        // Validate billing terms if Retainer type
        if (clientData.billingTerms.type === 'Retainer') {
            const hoursError = validateField('billingHours', clientData.billingTerms.hours);
            if (hoursError) {
                errors.billingHours = hoursError;
                isValid = false;
            }

            const chargeError = validateField('billingCharge', clientData.billingTerms.charge);
            if (chargeError) {
                errors.billingCharge = chargeError;
                isValid = false;
            }
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleBlur = (fieldName, value) => {
        const error = validateField(fieldName, value);
        if (error) {
            setFormErrors(prev => ({
                ...prev,
                [fieldName]: error,
            }));
        } else {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleCreateClient = () => {
        if (!validateAllFields()) {
            return;
        }

        const payload = canSeeInternal ? { ...clientData, tags: splitTagAssignments(tagEntries, null).tags } : clientData;
        dispatch(createClient(payload, (data) => {
                onSubmit(data);
                onClose();
            })
        );
    }

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogTitle>Create Client</DialogTitle>
            <DialogContent>
                <FormField>
                    <TextField
                        label="Name"
                        fullWidth
                        value={clientData.name}
                        onChange={(e) => {
                            setClientData({ ...clientData, name: e.target.value });
                            if (formErrors.name) {
                                setFormErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.name;
                                    return newErrors;
                                });
                            }
                        }}
                        onBlur={(e) => handleBlur("name", e.target.value)}
                        error={Boolean(formErrors.name)}
                        helperText={formErrors.name || ""}
                        required
                    />
                </FormField>
                <FormField>
                    <SectionTitle>Address Information</SectionTitle>

                    <TextField
                        label="Street"
                        fullWidth
                        value={clientData.address.street}
                        onChange={(e) => setClientData({ ...clientData, address: { ...clientData.address, street: e.target.value } })}
                    />
                    <TextField
                        label="Apartment/Suite"
                        fullWidth
                        value={clientData.address.apartment}
                        onChange={(e) => setClientData({ ...clientData, address: { ...clientData.address, apartment: e.target.value } })}
                    />

                    <Row>
                        <TextField
                            label="City"
                            fullWidth
                            value={clientData.address.city}
                            onChange={(e) => setClientData({ ...clientData, address: { ...clientData.address, city: e.target.value } })}
                        />
                        <TextField
                            label="State"
                            fullWidth
                            value={clientData.address.state}
                            onChange={(e) => setClientData({ ...clientData, address: { ...clientData.address, state: e.target.value } })}
                        />
                        <TextField
                            label="Zip Code"
                            fullWidth
                            value={clientData.address.postalCode}
                            onChange={(e) => setClientData({ ...clientData, address: { ...clientData.address, postalCode: e.target.value } })}
                        />
                    </Row>
                </FormField>
                <SectionTitle>Contact Details</SectionTitle>
                <FormField>
                    <Row>
                        <TextField
                            label="Email"
                            fullWidth
                            type="email"
                            value={clientData.email}
                            onChange={(e) => {
                                setClientData({ ...clientData, email: e.target.value });
                                if (formErrors.email) {
                                    setFormErrors((prev) => {
                                        const newErrors = { ...prev };
                                        delete newErrors.email;
                                        return newErrors;
                                    });
                                }
                            }}
                            onBlur={(e) => handleBlur("email", e.target.value)}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email || "josh.smith@example.com"}
                            required
                        />
                        <TextField
                            label="Phone"
                            fullWidth
                            type="tel"
                            value={clientData.phone}
                            onChange={(e) => {
                                setClientData({ ...clientData, phone: e.target.value });
                                if (formErrors.phone) {
                                    setFormErrors((prev) => {
                                        const newErrors = { ...prev };
                                        delete newErrors.phone;
                                        return newErrors;
                                    });
                                }
                            }}
                            onBlur={(e) => handleBlur("phone", e.target.value)}
                            error={Boolean(formErrors.phone)}
                            helperText={formErrors.phone || "+1 (XXX) XXX-XXXX"}
                            required
                            slotProps={{ htmlInput: { pattern: "^\\+?[\\d\\s\\-()]{1,25}$" } }}
                        />
                    </Row>
                </FormField>
                <FormField>
                    <TextField
                        label="Website"
                        fullWidth
                        value={clientData.website}
                        onChange={(e) => {
                            setClientData({ ...clientData, website: e.target.value });
                            if (formErrors.website) {
                                setFormErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.website;
                                    return newErrors;
                                });
                            }
                        }}
                        onBlur={(e) => handleBlur("website", e.target.value)}
                        error={Boolean(formErrors.website)}
                        helperText={formErrors.website || ""}
                    />
                </FormField>
                <FormField>
                    <Row>
                        <TextField
                            label="Client Relationship Manager"
                            fullWidth
                            value={clientData.clientManager || ""}
                            onChange={(e) => setClientData({ ...clientData, clientManager: e.target.value || null })}
                            select
                        >
                            <MenuItem value="">None</MenuItem>
                            {managers.map((user) => (
                                <MenuItem key={user._id} value={user._id}>
                                    {user.fullName}
                                </MenuItem>
                            ))}
                        </TextField>
                        {(canSeeInternal || canSeePrivate) && (
                            <TextField label="Privacy" fullWidth value={clientData.privacy} onChange={(e) => setClientData({ ...clientData, privacy: e.target.value })} select>
                                <MenuItem value="Public">Public</MenuItem>
                                <MenuItem value="Internal">Internal</MenuItem>
                                {canSeePrivate && <MenuItem value="Private">Private</MenuItem>}
                            </TextField>
                        )}
                    </Row>
                </FormField>
                {canSeeInternal && (
                    <FormField>
                        <TagPicker value={tagEntries} onChange={setTagEntries} source="all" clientId={null} />
                    </FormField>
                )}
                {canSeeInternal && canEditBillingTerms && (
                    <FormField>
                        <SectionTitle>Billing Configuration</SectionTitle>
                        <Row>
                            <TextField
                                label="Billing Type"
                                fullWidth
                                value={clientData.billingTerms.type}
                                onChange={(e) => setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, type: e.target.value } })}
                                select
                            >
                                <MenuItem value="Hourly">Hourly</MenuItem>
                                <MenuItem value="Retainer">Retainer</MenuItem>
                                <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                                <MenuItem value="Internal">Internal</MenuItem>
                                <MenuItem value="Non Billable">Non Billable</MenuItem>
                            </TextField>
                        </Row>
                        {clientData.billingTerms.type === "Retainer" && (
                            <Row>
                                <Column>
                                    <Row>
                                        <TextField
                                            label="Frequency"
                                            fullWidth
                                            value={clientData.billingTerms.frequency}
                                            onChange={(e) => setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, frequency: e.target.value } })}
                                            select
                                        >
                                            <MenuItem value="Monthly">Monthly</MenuItem>
                                            <MenuItem value="Weekly">Weekly</MenuItem>
                                            <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                                            <MenuItem value="Quarterly">Quarterly</MenuItem>
                                        </TextField>
                                        <TextField
                                            label="Hours"
                                            fullWidth
                                            type="number"
                                            value={clientData.billingTerms.hours}
                                            onChange={(e) => {
                                                setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, hours: e.target.value } });
                                                if (formErrors.billingHours) {
                                                    setFormErrors((prev) => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.billingHours;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            onBlur={(e) => handleBlur("billingHours", e.target.value)}
                                            error={Boolean(formErrors.billingHours)}
                                            helperText={formErrors.billingHours || ""}
                                        />
                                    </Row>
                                    <Row>
                                        <TextField
                                            label="Charge"
                                            fullWidth
                                            type="number"
                                            value={clientData.billingTerms.charge}
                                            onChange={(e) => {
                                                setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, charge: e.target.value } });
                                                if (formErrors.billingCharge) {
                                                    setFormErrors((prev) => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.billingCharge;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            onBlur={(e) => handleBlur("billingCharge", e.target.value)}
                                            error={Boolean(formErrors.billingCharge)}
                                            helperText={formErrors.billingCharge || ""}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="overage"
                                                    checked={clientData.billingTerms.overage}
                                                    onChange={(e) => setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, overage: e.target.checked } })}
                                                />
                                            }
                                            label="Overage"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="vip"
                                                    checked={clientData.billingTerms.vip}
                                                    onChange={(e) => setClientData({ ...clientData, billingTerms: { ...clientData.billingTerms, vip: e.target.checked } })}
                                                />
                                            }
                                            label="VIP"
                                        />
                                    </Row>
                                </Column>
                            </Row>
                        )}
                    </FormField>
                )}
            </DialogContent>
            <DialogActions>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={handleCreateClient}>Create</Button>
                    </>
                )}
            </DialogActions>
        </StyledDialog>
    );
};

export default CreateClientDialog;
